/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsCreate } from "../funcs/skillsCreate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RecordSkillResult } from "../models/components/recordskillresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateSkillRequest,
  CreateSkillSecurity,
} from "../models/operations/createskill.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateSkillMutationVariables = {
  request: CreateSkillRequest;
  security?: CreateSkillSecurity | undefined;
  options?: RequestOptions;
};

export type CreateSkillMutationData = RecordSkillResult;

export type CreateSkillMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * create skills
 *
 * @remarks
 * Record an uploaded SKILL.md. The implementation requires the skills product feature and skill write scope, and may create a skill, add a version to an existing skill, or return an existing canonical version as a no-op.
 */
export function useCreateSkillMutation(
  options?: MutationHookOptions<
    CreateSkillMutationData,
    CreateSkillMutationError,
    CreateSkillMutationVariables
  >,
): UseMutationResult<
  CreateSkillMutationData,
  CreateSkillMutationError,
  CreateSkillMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateSkillMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateSkill(): MutationKey {
  return ["@gram/client", "skills", "create"];
}

export function buildCreateSkillMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateSkillMutationVariables,
  ) => Promise<CreateSkillMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateSkill(),
    mutationFn: function createSkillMutationFn({
      request,
      security,
      options,
    }): Promise<CreateSkillMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsCreate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
